import { IMSARMBTSReport, IMSARMBTSRubricParameter } from '../../../../types/services.interfaces/mbts-ux';

export type TRubricLevel = 'highly-skilled' | 'proficient' | 'gap' | 'priority-gap';
export type TViewMode = 'narrative' | 'audit';
export type TProficiencyTone = 'practicing' | 'emerging' | 'proficient' | 'skilled';

export const BENCHMARK_PCT = 80;

export const SECTION_IDS = {
    mastery: 'arv2-mastery',
    lab: 'arv2-lab',
    habits: 'arv2-habits',
    audit: 'arv2-audit',
    feedback: 'arv2-feedback',
} as const;

export const getOverallScore = (report: IMSARMBTSReport): number => {
    if (!report.rubric_parameters.length) return 0;
    const avgPct = report.rubric_parameters.reduce((acc, curr) => acc + curr.score, 0) / report.rubric_parameters.length;
    return Number(((avgPct / 100) * 5).toFixed(1));
};

export const getProficiencyCategory = (score: number): string => {
    if (score <= 1.9) return 'Practicing Leader';
    if (score <= 2.9) return 'Emerging Leader';
    if (score <= 3.9) return 'Proficient Leader';
    return 'Highly Skilled Leader';
};

export const getProficiencyTone = (score: number): TProficiencyTone => {
    if (score <= 1.9) return 'practicing';
    if (score <= 2.9) return 'emerging';
    if (score <= 3.9) return 'proficient';
    return 'skilled';
};

export const getRubricLevel = (score: number): { title: string; level: TRubricLevel; color: string } => {
    if (score >= 75) return { title: 'Highly Skilled', level: 'highly-skilled', color: '#059669' };
    if (score >= 50) return { title: 'Proficient', level: 'proficient', color: '#4338CA' };
    if (score >= 25) return { title: 'Gap', level: 'gap', color: '#D48A09' };
    return { title: 'Priority Gap', level: 'priority-gap', color: '#9F1239' };
};

export const getElapsedLabel = (sessionStart: string, timestamp: string): string => {
    const start = new Date(sessionStart).getTime();
    const ts = new Date(timestamp).getTime();
    if (Number.isNaN(start) || Number.isNaN(ts)) return '00:00';

    const elapsedSeconds = Math.max(0, Math.floor((ts - start) / 1000));
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const getSimulationDurationLabel = (report: IMSARMBTSReport): string | null => {
    const sessions = report.rapport_collapses_conversations;
    if (!sessions.length) return null;

    const totalMs = sessions.reduce((acc, session) => {
        const start = new Date(session.session_start).getTime();
        const end = new Date(session.session_end).getTime();
        if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return acc;
        return acc + (end - start);
    }, 0);

    if (!totalMs) return null;

    const totalSeconds = Math.floor(totalMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
};

export const getLowestSkill = (parameters: IMSARMBTSRubricParameter[]): IMSARMBTSRubricParameter | null => {
    if (!parameters.length) return null;
    return [...parameters].sort((a, b) => a.score - b.score)[0];
};

export const scrollToSectionId = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const stripHtml = (html: string): string => html.replace(/<[^>]+>/g, '').trim();

export const getNarrativeParagraphs = (executiveNarrative: string): string[] => {
    const matches = executiveNarrative.match(/<p>.*?<\/p>/g) || [];
    return matches.map(stripHtml).filter(Boolean);
};

export const getHeadline = (report: IMSARMBTSReport): string => {
    const paragraphs = getNarrativeParagraphs(report.executive_narrative);
    if (!paragraphs.length) {
        return `Your ${report.cognitive_profile.toLowerCase()} profile shapes how you show up in high-stakes moments.`;
    }

    const first = paragraphs[0];
    const sentenceMatch = first.match(/^[^.!?]+[.!?]?/);
    const sentence = (sentenceMatch?.[0] || first).trim();
    return sentence.length > 160 ? `${sentence.slice(0, 157).trim()}...` : sentence;
};

export const getNarrativeBodyHtml = (report: IMSARMBTSReport): string => {
    const matches = report.executive_narrative.match(/<p>.*?<\/p>/g) || [];
    const content = matches.length > 1 ? matches.slice(0, 3) : matches;
    return content.join('');
};

export const getFrictionLabel = (report: IMSARMBTSReport): string => {
    const collapses = report.rapport_collapses_conversations.flatMap(c => c.rapport_collapses);
    if (!collapses.length) return 'Review interaction moments';

    const first = collapses[0];
    const session = report.rapport_collapses_conversations.find(
        c => c.conversation_id === first.chat.conversation_id
    );

    if (!session) return first.critical_turn || 'Session highlight';

    return `Session Highlight @ ${getElapsedLabel(session.session_start, first.chat.created_at)}`;
};

export const getQuickWinLabel = (report: IMSARMBTSReport): string => {
    const lowest = getLowestSkill(report.rubric_parameters);
    return lowest?.testing_skill.title || 'Focus recommendations';
};
